'use client';

import { ComponentProps, ReactNode } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';

export type ActionMenuItem = {
  icon: IconProp;
  label: string;
  onClick?: () => void;
  /** Renders the row in the destructive (red) style, e.g. "Delete". */
  danger?: boolean;
};

/** A menu entry is either an action or a visual separator. */
export type ActionMenuEntry = ActionMenuItem | 'separator';

type Anchor = ComponentProps<typeof MenuItems>['anchor'];

type Props = {
  /** Trigger content, rendered inside the menu button. Receives the open state. */
  trigger: (state: { open: boolean }) => ReactNode;
  items: ActionMenuEntry[];
  /** Class for the `Menu` root (e.g. positioning/spacing). */
  className?: string;
  triggerClassName?: string;
  triggerAriaLabel?: string;
  /** Stop the trigger/item clicks from bubbling (e.g. when nested in a clickable row). */
  stopPropagation?: boolean;
  anchor?: Anchor;
  panelClassName?: string;
  /** `sm` (default) matches the small pill buttons; `md` the list rows. */
  size?: 'sm' | 'md';
};

// A dropdown action menu with a flexible trigger and consistently-styled rows
// (icon + label, optional red destructive rows, separators). Shared by the
// header/rail account menus and the box/collection option menus. Rows use
// the typography of the small pill buttons: 13px, outline icons in grey.
// Pass regular (outline) icons.
const ActionMenu = ({
  trigger,
  items,
  className,
  triggerClassName,
  triggerAriaLabel,
  stopPropagation = false,
  anchor = 'bottom end',
  panelClassName,
  size = 'sm',
}: Props) => (
  <Menu as="div" className={className}>
    <MenuButton
      className={triggerClassName}
      aria-label={triggerAriaLabel}
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
    >
      {(state) => <>{trigger(state)}</>}
    </MenuButton>

    <MenuItems
      transition
      anchor={anchor}
      className={classNames(styles.panel, panelClassName)}
    >
      {items.map((entry, index) =>
        entry === 'separator' ? (
          <div key={`separator-${index}`} className={styles.separator} />
        ) : (
          <MenuItem key={entry.label}>
            <button
              type="button"
              onClick={(event) => {
                if (stopPropagation) event.stopPropagation();
                entry.onClick?.();
              }}
              className={classNames(
                styles.item.base,
                styles.size[size].item,
                entry.danger ? styles.item.danger : styles.item.normal,
              )}
            >
              <FontAwesomeIcon
                icon={entry.icon}
                className={classNames(
                  styles.item.icon,
                  styles.size[size].icon,
                  entry.danger
                    ? styles.item.dangerIcon
                    : styles.item.normalIcon,
                )}
                aria-hidden="true"
              />
              {entry.label}
            </button>
          </MenuItem>
        ),
      )}
    </MenuItems>
  </Menu>
);

export default ActionMenu;

const styles = {
  // As wide as its longest label.
  panel: classNames(
    'mt-1.5 min-w-36 rounded-xl bg-surface-overlay p-1.5 shadow-lg ring-1 ring-hairline z-40',
    'transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none',
    'data-closed:scale-95 data-closed:opacity-0',
  ),
  separator: 'my-1 border-t border-hairline',
  item: {
    base: classNames(
      'flex w-full items-center whitespace-nowrap rounded-lg py-2 pl-2.5 pr-4 text-left',
      'font-medium',
    ),
    normal: 'text-ink-primary data-focus:bg-ink-secondary/8',
    danger: 'text-mf-red-600 data-focus:bg-mf-red-500/10 dark:text-mf-red-400',
    icon: 'fa-fw',
    normalIcon: 'text-ink-secondary',
    dangerIcon: 'text-mf-red-600 dark:text-mf-red-400',
  },
  size: {
    sm: { item: 'gap-2.5 text-[13px]', icon: 'text-xs' },
    md: { item: 'gap-3 text-sm', icon: 'text-[13px]' },
  },
};
